import { Button, DialogV2, Typography } from '@recruit-robin/robin-theme/web-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { JobSummaryViewModel } from '@/features/job-improved-selection';
import { JobImprovedSelect } from '@/features/job-improved-selection';
import { useDictionary } from '@/shared/lib/i18n';

import { JobQuality, getJobQuality } from '@/entities/job';

import { useStyles } from './SelectJobModal.styles';
import type { SelectJobModalProps } from './SelectJobModal.types';

export function SelectJobModal({
  isVisible,
  onConfirm,
  onCancel,
  onSelectJob,
  footer,
  initialJob,
  onEditJob,
  slot,
  isConfirmDisabled,
}: SelectJobModalProps) {
  const classes = useStyles({});
  const dictionary = useDictionary();
  const [selectedJob, setSelectedJob] = useState<JobSummaryViewModel | null>(initialJob ?? null);

  useEffect(() => {
    setSelectedJob(initialJob ?? null);
  }, [initialJob]);

  const handleConfirm = useCallback(() => {
    if (!selectedJob) return;
    onConfirm(selectedJob);
  }, [onConfirm, selectedJob]);

  const handleSelectionChange = useCallback(
    (job: JobSummaryViewModel | null) => {
      setSelectedJob(job);
      onSelectJob(job);
    },
    [onSelectJob],
  );

  const quality = useMemo(() => {
    if (!selectedJob || !selectedJob.qualityPercent) return null;
    return getJobQuality(selectedJob.qualityPercent);
  }, [selectedJob]);

  const description = useMemo(() => {
    if (!selectedJob) return dictionary.selectJobModalIntroMessage;

    switch (quality) {
      case JobQuality.HIGH:
        return dictionary.activateRobinJrDescription;
      case JobQuality.MEDIUM:
        return dictionary.selectJobModalInsufficientJobInfo;
      case JobQuality.LOW:
        return dictionary.selectJobModalInsufficientJobInfo;
    }
  }, [dictionary, quality]);

  if (!isVisible) return null;

  return (
    <DialogV2.Root isVisible={true} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Close onRequestClose={onCancel} />
        <DialogV2.Header customCss={classes.header}>
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {dictionary.activateRobinJrTitle}
          </Typography>
        </DialogV2.Header>

        <DialogV2.Body>
          <div css={classes.body}>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {description}
            </Typography>

            <div css={classes.selectWrapper}>
              <JobImprovedSelect
                enabled={isVisible}
                selectedJob={selectedJob}
                onSelectionChange={handleSelectionChange}
                {...(onEditJob ? { onEditJob } : {})}
              />
            </div>
            {slot && <div css={classes.slotSection}>{slot}</div>}

            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {dictionary.activateRobinJrTrackProgress}
            </Typography>
          </div>
        </DialogV2.Body>

        <DialogV2.Footer customCss={classes.footer}>
          {footer}
          <div css={classes.footerActions}>
            <Button label={dictionary.cancel} onClick={onCancel} style="text" type="subtle2" />
            <Button
              label={dictionary.confirmSelection}
              onClick={handleConfirm}
              style="filled"
              type="success"
              disabled={!selectedJob || quality !== JobQuality.HIGH || isConfirmDisabled}
            />
          </div>
        </DialogV2.Footer>
      </DialogV2.Content>
    </DialogV2.Root>
  );
}
